import { ApolloError } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Skeleton, Stack, Typography } from '@mui/material';
import { MembershipsConfigViewForm } from 'src/pages/Settings/memberships/components/memberships-config-view-form';
import { DEFAULT_PLANS_TAB_VALUES } from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import { type PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import { configLoadingSkeletonSx } from 'src/pages/Settings/memberships/styles';
import type { ConfigFormSaveSlots } from 'src/pages/Settings/memberships/types';
import { parseMembershipsConfigError } from 'src/pages/Settings/memberships/utils/parse-memberships-config-error';

type MembershipsConfigPanelContentProps = {
  error: unknown;
  isLoading: boolean;
  hasSegments: boolean;
  selectedSegmentId: string | null;
  data: PlansTabFormValues | null;
  isNewConfig: boolean;
  showCreateConfig: boolean;
  onCreateConfig: () => void;
  isCreatingConfig: boolean;
  readOnly: boolean;
  formKey: string;
  isApplyingSave: boolean;
  saveSlots: ConfigFormSaveSlots<PlansTabFormValues>;
  onFormValuesChange: (values: PlansTabFormValues) => void;
};

export const MembershipsConfigPanelContent = ({
  error,
  isLoading,
  hasSegments,
  selectedSegmentId,
  data,
  isNewConfig,
  showCreateConfig,
  onCreateConfig,
  isCreatingConfig,
  readOnly,
  formKey,
  isApplyingSave,
  saveSlots,
  onFormValuesChange,
}: MembershipsConfigPanelContentProps) => {
  if (error) {
    return (
      <Typography p={2} variant="body2" color="error">
        {error instanceof ApolloError
          ? parseMembershipsConfigError(error)
          : 'Failed to load configuration.'}
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Stack p={2} gap={2} sx={configLoadingSkeletonSx}>
        <Skeleton variant="rounded" height="2.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
      </Stack>
    );
  }

  if (!hasSegments) {
    return (
      <Typography p={2} variant="body2" color="text.secondary">
        No client segments are configured for this partner yet.
      </Typography>
    );
  }

  if (!selectedSegmentId) {
    return (
      <Typography p={2} variant="body2" color="text.secondary">
        Select a client segment to view configuration.
      </Typography>
    );
  }

  if (!data && !isNewConfig) {
    return (
      <Stack p={2} gap={2}>
        <Typography variant="body2" color="text.secondary" align="center">
          No initial configuration found.
        </Typography>
        {showCreateConfig ? (
          <Stack alignItems="center">
            <LoadingButton
              variant="outlined"
              onClick={onCreateConfig}
              loading={isCreatingConfig}
              disabled={readOnly || isCreatingConfig}
            >
              Create configuration
            </LoadingButton>
          </Stack>
        ) : null}
      </Stack>
    );
  }

  return (
    <MembershipsConfigViewForm
      key={formKey}
      data={data ?? DEFAULT_PLANS_TAB_VALUES}
      readOnly={readOnly}
      isApplyingSave={isApplyingSave}
      onFormValuesChange={onFormValuesChange}
      {...saveSlots}
    />
  );
};
